import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ExternalLink, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCommunity } from "@/context/CommunityContext";
import ResourcePage from "@/admin/ResourcePage";
import { AdminDashboard, AdminReporting } from "@/admin/AdminDashboard";
import { AdminConfirmations, AdminIdeas, AdminLoveMessages, AdminReports, AdminUsers } from "@/admin/AdminModeration";
import PitchPrint from "@/admin/PitchPrint";
import PilotImages from "@/admin/PilotImages";

const NAV = [
  ["", "Dashboard"],
  ["pitch", "SPUREN · Pitch / Druck"],
  ["pilot-images", "SPUREN · Bilder / Karten"],
  ["communities", "Gemeinden"],
  ["walks", "Wege"],
  ["stations", "Stationen"],
  ["artworks", "Kunstwerke"],
  ["artwork_credits", "Werk-Credits"],
  ["walk_segments", "Wegabschnitte"],
  ["places", "Orte"],
  ["discovery_items", "Entdeckungsinhalte"],
  ["contributors", "Mitwirkende"],
  ["challenges", "Challenges"],
  ["games", "Spiele"],
  ["feature_cards", "Besonderes"],
  ["love_tree_configs", "Liebesbaum-Einstellungen"],
  ["love-messages", "Liebesbaum"],
  ["achievements", "Erfolge"],
  ["users", "Benutzer"],
  ["confirmations", "Bestätigungen"],
  ["ideas", "Ideen"],
  ["reports", "Meldungen"],
  ["reporting", "Reporting"],
];

export default function AdminApp() {
  const { user, loading, isAdmin, logout } = useAuth();
  const { communities, slug, setSlug } = useCommunity();
  const location = useLocation();
  useEffect(() => {
    if (!user || user.role === "PLATFORM_ADMIN") return;
    const own = communities.find((c) => c.id === user.community_id);
    if (own && own.slug !== slug) setSlug(own.slug);
  }, [user, communities, slug, setSlug]);

  if (loading) return <div className="p-10 text-center text-sm text-[var(--ink-soft)]">wird geladen …</div>;
  if (!user || !isAdmin)
    return (
      <div className="mx-auto max-w-md p-12 text-center" data-testid="admin-denied">
        <h1 className="text-3xl">Adminbereich</h1>
        <p className="mt-3 text-sm text-[var(--ink-soft)]">Bitte melde dich mit einem Administratorkonto an.</p>
        <Link to="/login" className="btn btn-primary mt-6" data-testid="admin-login-link">ANMELDEN</Link>
      </div>
    );

  const current = location.pathname.replace(/^\/admin\/?/, "");
  const isPlatform = user.role === "PLATFORM_ADMIN";
  const ownContent = ["walks", "stations", "artworks", "artwork_credits", "walk_segments", "places", "discovery_items", "contributors", "challenges", "games", "feature_cards", "love_tree_configs", "achievements"];
  const visibleNav = isPlatform ? NAV : NAV.filter(([path]) => ownContent.includes(path));
  const visibleCommunities = isPlatform ? communities : communities.filter((c) => c.id === user.community_id);
  if (!isPlatform && !ownContent.includes(current.split("/")[0])) return <Navigate to="/admin/walks" replace />;

  return (
    <div className="flex min-h-screen" data-testid="admin-app">
      <aside className="hidden w-64 shrink-0 border-r bg-white lg:block">
        <div className="p-5">
          <p className="text-2xl">Verwaltung</p>
          <p className="mt-1 text-xs text-[var(--ink-soft)]">{user.display_name}</p>
          <select className="field mt-4 !py-2 !text-xs" value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="admin-community-select">
            {visibleCommunities.map((c) => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>
        </div>
        <nav className="px-2 pb-8">
          {visibleNav.map(([path, label]) => (
            <Link
              key={path}
              to={`/admin/${path}`}
              className="block rounded-lg px-3 py-2 text-sm transition-colors"
              style={current === path ? { background: "var(--brand)", color: "#fff" } : {}}
              data-testid={`admin-nav-${path || "dashboard"}`}
            >
              {label}
            </Link>
          ))}
          <Link to="/" className="mt-4 block rounded-lg px-3 py-2 text-sm underline" data-testid="admin-to-app">
            <ExternalLink className="mr-1 inline h-3.5 w-3.5" /> Zur App
          </Link>
          <button className="mt-1 block w-full rounded-lg px-3 py-2 text-left text-sm underline" onClick={logout} data-testid="admin-logout">
            <LogOut className="mr-1 inline h-3.5 w-3.5" /> Abmelden
          </button>
        </nav>
      </aside>

      <main className="min-w-0 flex-1 p-5 lg:p-10">
        <div className="mb-5 flex flex-wrap gap-2 lg:hidden">
          <select className="field !w-auto !py-2 !text-xs" value={slug} onChange={(e) => setSlug(e.target.value)}>
            {visibleCommunities.map((c) => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>
          <select
            className="field !w-auto !py-2 !text-xs"
            value={current}
            onChange={(e) => { window.location.href = `/admin/${e.target.value}`; }}
            data-testid="admin-nav-mobile"
          >
            {visibleNav.map(([path, label]) => (
              <option key={path} value={path}>{label}</option>
            ))}
          </select>
        </div>
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="pitch" element={<PitchPrint />} />
          <Route path="pilot-images" element={<PilotImages />} />
          <Route path="communities" element={<ResourcePage resource="communities" />} />
          <Route path="walks" element={<ResourcePage resource="walks" />} />
          <Route path="stations" element={<ResourcePage resource="stations" />} />
          <Route path="artworks" element={<ResourcePage resource="artworks" />} />
          <Route path="artwork_credits" element={<ResourcePage resource="artwork_credits" />} />
          <Route path="walk_segments" element={<ResourcePage resource="walk_segments" />} />
          <Route path="places" element={<ResourcePage resource="places" />} />
          <Route path="discovery_items" element={<ResourcePage resource="discovery_items" />} />
          <Route path="contributors" element={<ResourcePage resource="contributors" />} />
          <Route path="challenges" element={<ResourcePage resource="challenges" />} />
          <Route path="games" element={<ResourcePage resource="games" />} />
          <Route path="feature_cards" element={<ResourcePage resource="feature_cards" />} />
          <Route path="love_tree_configs" element={<ResourcePage resource="love_tree_configs" />} />
          <Route path="achievements" element={<ResourcePage resource="achievements" />} />
          <Route path="love-messages" element={<AdminLoveMessages />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="confirmations" element={<AdminConfirmations />} />
          <Route path="ideas" element={<AdminIdeas />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="reporting" element={<AdminReporting />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}
